import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { QueryKeys, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { reloadRequestList } from 'pages/requestList/actions';
import {
    canShowNextBillPromo,
    canShowNextPOPromo,
    closeBillToPoMatchingBill,
    closeBillToPoMatchingPO,
    closeGoodsReceivedNotePO,
    isApprovedPO,
    isBillToPoMatchingBillClosed,
    isBillToPoMatchingPOClosed,
    isEnabledBillAndPOWorkflows,
    isGoodsReceivedNotePOClosed,
    isUnprocessedBill,
} from 'pages/requestList/utils/featurePromotions';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDeleteMatching } from 'shared/data/webApp/v1';
import { isAllFeaturesTrialStartAllowed, isPayOrCapturePromotionsAllowed } from 'shared/helpers';
import { useAddonPromotions } from 'shared/hooks/useAddonPromotions';
import { AddonPromotionType, AftPromotionType, pageVisiblePromotionsState } from 'shared/states';

export const useQBooksMatchingRemoveData = (companyId: string) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const { mutateAsync, isLoading } = useDeleteMatching({
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.QBOOKS_MATCHING_INFO]);
            dispatch(reloadRequestList());
        },
    });

    const onMatchingRemove = useCallback(
        (lineItemId: string) =>
            mutateAsync({
                body: { companyId, lineItemId },
            }),
        [mutateAsync, companyId]
    );

    return {
        isLoading,
        onMatchingRemove,
    };
};

export const useGetNextPromotions = () => {
    const { canShowPromotion } = useAddonPromotions();

    const getNextPromotion = useCallback(
        (
            request: selectors.types.ExpandedRequest,
            company: selectors.types.ExpandedCompany,
            workflows: selectors.types.ExpandedTemplate[]
        ) => {
            if (request.integrationCode === domain.IntegrationCode.QBooksBill) {
                const isLastApprovalStep =
                    request.activeStep && request.steps[request.steps.length - 1]?.id === request.activeStep.id;
                const isApproved = request.statusV2 === domain.RequestStatusV2.Approved;

                if (
                    isPayOrCapturePromotionsAllowed(company, AddonPromotionType.Pay) &&
                    canShowPromotion(AddonPromotionType.Pay) &&
                    (isLastApprovalStep || isApproved)
                ) {
                    return AddonPromotionType.Pay;
                }

                const notUploadedViaCapture = request.attachments.every((attachment) => !attachment.ocrStatus);

                if (
                    isPayOrCapturePromotionsAllowed(company, AddonPromotionType.Capture) &&
                    canShowPromotion(AddonPromotionType.Capture) &&
                    notUploadedViaCapture &&
                    company.flags.isManager
                ) {
                    return AddonPromotionType.Capture;
                }
            }

            if (!isAllFeaturesTrialStartAllowed(company, domain.IntegrationType.QBooks)) {
                return null;
            }

            if (request.integrationCode === domain.IntegrationCode.QBooksBill) {
                if (!canShowNextBillPromo()) {
                    return null;
                }

                if (
                    !isBillToPoMatchingBillClosed() &&
                    isUnprocessedBill(request) &&
                    isEnabledBillAndPOWorkflows(workflows, domain.IntegrationType.QBooks)
                ) {
                    return AftPromotionType.BillToPoMatchingBill;
                }
            } else if (request.integrationCode === domain.IntegrationCode.QBooksPo) {
                if (!canShowNextPOPromo()) {
                    return null;
                }

                if (!isApprovedPO(request)) {
                    return null;
                }

                if (isEnabledBillAndPOWorkflows(workflows, domain.IntegrationType.QBooks)) {
                    if (!isBillToPoMatchingPOClosed()) {
                        return AftPromotionType.BillToPoMatchingPO;
                    } else if (!isGoodsReceivedNotePOClosed()) {
                        return AftPromotionType.GoodsReceivedNote;
                    }
                } else {
                    if (!isGoodsReceivedNotePOClosed()) {
                        return AftPromotionType.GoodsReceivedNote;
                    }
                }
            }

            return null;
        },
        [canShowPromotion]
    );

    return getNextPromotion;
};

export const useClosePromotion = () => {
    const [pageVisiblePromotions, setPageVisiblePromotions] = useAtom(pageVisiblePromotionsState);
    const { closePromotion } = useAddonPromotions();

    const handleClosePromotion = useCallback(
        (promotion: AftPromotionType | AddonPromotionType) => {
            if (!pageVisiblePromotions) {
                return;
            }

            switch (promotion) {
                case AddonPromotionType.Capture:
                case AddonPromotionType.Pay:
                    closePromotion(promotion);

                    break;
                case AftPromotionType.BillToPoMatchingBill:
                    closeBillToPoMatchingBill();

                    break;

                case AftPromotionType.BillToPoMatchingPO:
                    closeBillToPoMatchingPO();
                    break;

                case AftPromotionType.GoodsReceivedNote:
                    closeGoodsReceivedNotePO();
                    break;
            }

            setPageVisiblePromotions(null);
        },
        [pageVisiblePromotions, setPageVisiblePromotions, closePromotion]
    );

    return handleClosePromotion;
};
